import { useEffect, useState } from 'react';
import { Card, Button, List, Typography, Space, message, Tabs, Tag, Modal, Input, Select, Divider, Upload } from 'antd';
import { CalendarOutlined, CheckSquareOutlined, ApartmentOutlined, CheckOutlined, ClockCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { CalendarService } from '../api/services/calendar.service';
import { BPMService } from '../api/services/bpm.service';
import { TasksService } from '../api/services/tasks.service';
import { UsersService } from '../api/services/users.service';
import api from '../api/client';
import ProcessDetailModal from '../components/ProcessDetailModal';
import { useFileViewer } from '../store/FileViewerContext';
import { useAuth } from '../store/auth';

const { Title, Text } = Typography;

export default function ActionItemsPage() {
  const navigate = useNavigate();
  const { openFileCard } = useFileViewer();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [rsvps, setRsvps] = useState([]);
  const [rsvpHistory, setRsvpHistory] = useState([]);
  const [bpmPending, setBpmPending] = useState([]);
  const [bpmHistory, setBpmHistory] = useState([]);
  const [taskValidations, setTaskValidations] = useState([]);
  const [taskValidationsHistory, setTaskValidationsHistory] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [checklistsHistory, setChecklistsHistory] = useState([]);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [users, setUsers] = useState([]);
  
  // BPM Approval Modal State
  const [bpmModalVisible, setBpmModalVisible] = useState(false);
  const [bpmComment, setBpmComment] = useState('');
  const [bpmCurrentAction, setBpmCurrentAction] = useState({ stepId: null, status: null });

  // Process Details Modal State
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [currentProcess, setCurrentProcess] = useState(null);

  // BPM Delegation Modal State
  const [delegateModalVisible, setDelegateModalVisible] = useState(false);
  const [delegateTarget, setDelegateTarget] = useState(null);
  const [delegateStepId, setDelegateStepId] = useState(null);

  // BPM Restart Modal State (for editor)
  const [restartModalVisible, setRestartModalVisible] = useState(false);
  const [restartTarget, setRestartTarget] = useState(null);
  const [restartFiles, setRestartFiles] = useState([]);
  const [restartLoading, setRestartLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [
        rsvpsRes, rsvpHistRes, 
        bpmRes, bpmHistRes, 
        tasksRes, tasksHistRes, 
        checklistsRes, checkHistRes, 
        overdueRes,
        usersRes
      ] = await Promise.all([
        CalendarService.getPendingRsvps(),
        CalendarService.getRsvpHistory(),
        BPMService.getPending(),
        BPMService.getHistory(),
        TasksService.getPendingValidations(),
        TasksService.getValidationsHistory(),
        TasksService.getPendingChecklists(),
        TasksService.getChecklistsHistory(),
        user ? TasksService.getTasks({ assignee_id: user.id, overdue: true, limit: 100 }) : Promise.resolve({ data: [] }),
        UsersService.getUsers()
      ]);
      setRsvps(rsvpsRes.data);
      setRsvpHistory(rsvpHistRes.data);
      setBpmPending(bpmRes.data);
      setBpmHistory(bpmHistRes.data);
      setTaskValidations(tasksRes.data);
      setTaskValidationsHistory(tasksHistRes.data);
      setChecklists(checklistsRes.data);
      setChecklistsHistory(checkHistRes.data);
      setOverdueTasks(overdueRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error(err);
      message.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRsvp = async (eventId, status) => {
    try {
      await api.patch(`/calendar/events/${eventId}/rsvp`, null, { params: { status } });
      message.success(status === 'accepted' ? 'Участие подтверждено' : 'Участие отклонено');
      fetchData();
    } catch (err) {
      message.error('Ошибка обновления статуса');
    }
  };

  const handleChecklistComplete = async (taskId, itemId) => {
    try {
      await TasksService.updateChecklistItem(taskId, itemId, { is_completed: true });
      message.success('Пункт выполнен');
      fetchData();
    } catch (err) {
      message.error('Ошибка обновления чек-листа');
    }
  };

  const openBpmModal = (stepId, status) => {
    setBpmCurrentAction({ stepId, status });
    setBpmComment('');
    setBpmModalVisible(true);
  };

  const submitBpmApprove = async () => {
    if (!bpmComment.trim()) {
      message.warning('Комментарий обязателен');
      return;
    }
    
    try {
      await BPMService.approveStep(bpmCurrentAction.stepId, { 
        status: bpmCurrentAction.status, 
        comment: bpmComment 
      });
      message.success(bpmCurrentAction.status === 'approved' ? 'Согласовано' : 'Отклонено');
      setBpmModalVisible(false);
      fetchData();
    } catch (err) {
      message.error('Ошибка обработки согласования');
    }
  };

  const openDelegateModal = (stepId) => {
    setDelegateStepId(stepId);
    setDelegateTarget(null);
    setDelegateModalVisible(true);
  };

  const submitDelegate = async () => {
    if (!delegateTarget) {
      message.warning('Выберите сотрудника');
      return;
    }
    try {
      await BPMService.delegateStep(delegateStepId, { new_approver_id: delegateTarget });
      message.success('Согласование делегировано');
      setDelegateModalVisible(false);
      fetchData();
    } catch (err) {
      message.error('Ошибка при делегировании');
    }
  };

  const openRestartModal = (step) => {
    setRestartTarget(step);
    setRestartFiles([]);
    setRestartModalVisible(true);
  };

  const submitRestart = async () => {
    if (restartFiles.length === 0) {
      message.warning('Прикрепите файлы');
      return;
    }
    setRestartLoading(true);
    try {
      const formData = new FormData();
      restartFiles.forEach(f => formData.append('files', f.originFileObj || f));
      
      await api.post(`/bpm/instance/${restartTarget.instance_id}/restart`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      message.success(`Круг ${restartTarget.round_number + 1} запущен`);
      setRestartModalVisible(false);
      fetchData();
    } catch (err) {
      message.error('Ошибка при запуске круга');
    } finally {
      setRestartLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Требуется действие</h1>
        <p className="page-subtitle">Задачи, требующие вашего внимания</p>
      </div>
      
      <Tabs defaultActiveKey="1" items={[
        {
          key: '1',
          label: <span><CalendarOutlined /> Приглашения ({rsvps.length})</span>,
          children: (
            <>
            <List
              loading={loading}
              dataSource={rsvps}
              locale={{ emptyText: 'Нет новых приглашений' }}
              renderItem={(event) => (
                <List.Item>
                  <Card style={{ width: '100%' }} size="small">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text strong style={{ fontSize: 16 }}>{event.title}</Text>
                        <br/>
                        <Text type="secondary">
                          {dayjs(event.start_time).format('DD.MM.YYYY HH:mm')} - {dayjs(event.end_time).format('HH:mm')}
                        </Text>
                        {event.location && <div><Text type="secondary">Место: {event.location}</Text></div>}
                      </div>
                      <Space>
                        <Button type="primary" onClick={() => handleRsvp(event.id, 'accepted')}>Принять</Button>
                        <Button danger onClick={() => handleRsvp(event.id, 'declined')}>Отклонить</Button>
                      </Space>
                    </div>
                  </Card>
                </List.Item>
              )}
            />
            
            <Divider>История ответов</Divider>
            <div style={{ maxHeight: 'calc(25 * 70px)', overflowY: 'auto' }}>
              <List
                dataSource={rsvpHistory}
                locale={{ emptyText: 'Нет истории ответов' }}
                renderItem={(event) => (
                  <List.Item>
                    <Card style={{ width: '100%', opacity: 0.8 }} size="small">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <Text strong>{event.title}</Text>
                          <br/>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {dayjs(event.start_time).format('DD.MM.YYYY HH:mm')}
                          </Text>
                        </div>
                        <Tag color={event.user_status === 'accepted' ? 'success' : 'error'}>
                          {event.user_status === 'accepted' ? 'Принято' : 'Отклонено'}
                        </Tag>
                      </div>
                    </Card>
                  </List.Item>
                )}
              />
            </div>
            </>
          )
        },
        {
          key: '2',
          label: <span><ApartmentOutlined /> Согласования ({bpmPending.length})</span>,
          children: (
            <>
            <List
              loading={loading}
              dataSource={bpmPending}
              locale={{ emptyText: 'Нет процессов для согласования' }}
              renderItem={(step) => {
                if (step.is_editor) {
                  return (
                    <List.Item>
                      <Card style={{ width: '100%', borderColor: '#faad14' }} size="small">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <Text strong style={{ fontSize: 16 }}>
                              <a onClick={() => {
                                setCurrentProcess({ id: step.instance_id, title: step.process_title, status: 'active', target_file_name: step.target_file_name, created_at: step.created_at });
                                setDetailModalOpen(true);
                              }} style={{ color: '#1890ff' }}>
                                {step.process_title}
                              </a>
                            </Text>
                            <Space style={{ display: 'block', marginTop: 4 }}>
                              <Tag color="orange">Редактор документа</Tag>
                              <Tag color="cyan">Раунд {step.round_number}</Tag>
                              <Tag color="cyan">
                                Документ:{' '}
                                <a onClick={() => openFileCard(step.target_file_id)} style={{ color: '#1890ff' }}>
                                  {step.target_file_name}
                                </a>
                              </Tag>
                              {step.project_name && (
                                <Tag color="geekblue" style={{ cursor: 'pointer' }} onClick={() => navigate(`/projects?projectId=${step.project_id}`)}>
                                  {step.project_name}
                                </Tag>
                              )}
                            </Space>
                          </div>
                          <Space>
                            {step.task_id && <Button onClick={() => navigate(`/tasks?taskId=${step.task_id}`)}>Перейти к задаче</Button>}
                            <Button type="primary" onClick={() => openRestartModal(step)}>
                              Запустить круг {step.round_number + 1}
                            </Button>
                          </Space>
                        </div>
                      </Card>
                    </List.Item>
                  );
                }
                
                return (
                  <List.Item>
                    <Card style={{ width: '100%' }} size="small">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <Text strong style={{ fontSize: 16 }}>
                            <a onClick={() => {
                              setCurrentProcess({ id: step.instance_id, title: step.process_title, status: 'active', target_file_name: step.target_file_name, created_at: step.created_at });
                              setDetailModalOpen(true);
                            }} style={{ color: '#1890ff' }}>
                              {step.process_title}
                            </a>
                          </Text>
                          <Space style={{ display: 'block', marginTop: 4 }}>
                            <Tag color="purple">Этап {step.stage_number + 1}</Tag>
                            <Tag color="cyan">
                              Документ:{' '}
                              <a onClick={() => openFileCard(step.target_file_id)} style={{ color: '#1890ff' }}>
                                {step.target_file_name}
                              </a>
                            </Tag>
                            {step.project_name && (
                              <Tag color="geekblue" style={{ cursor: 'pointer' }} onClick={() => navigate(`/projects?projectId=${step.project_id}`)}>
                                {step.project_name}
                              </Tag>
                            )}
                          </Space>
                        </div>
                        <Space>
                          {step.task_id && <Button onClick={() => navigate(`/tasks?taskId=${step.task_id}`)}>Перейти к задаче</Button>}
                          <Button onClick={() => openDelegateModal(step.id)}>Делегировать</Button>
                          <Button type="primary" onClick={() => openBpmModal(step.id, 'approved')}>Согласовать</Button>
                          <Button danger onClick={() => openBpmModal(step.id, 'rejected')}>Отклонить</Button>
                        </Space>
                      </div>
                    </Card>
                  </List.Item>
                );
              }}
            />
            
            <Divider>История согласований</Divider>
            <div style={{ maxHeight: 'calc(25 * 70px)', overflowY: 'auto' }}>
              <List
                dataSource={bpmHistory}
                locale={{ emptyText: 'Нет завершенных согласований' }}
                renderItem={(step) => (
                  <List.Item>
                    <Card style={{ width: '100%', opacity: 0.8 }} size="small">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <Text strong>
                            <a onClick={() => {
                              setCurrentProcess({ id: step.instance_id, title: step.process_title, status: step.is_editor ? step.status : 'completed', target_file_name: step.target_file_name, created_at: step.created_at });
                              setDetailModalOpen(true);
                            }} style={{ color: '#1890ff' }}>
                              {step.process_title}
                            </a>
                          </Text>
                          <br/>
                          <Space style={{ marginTop: 4 }}>
                            {step.is_editor && <Tag color="orange">Редактор</Tag>}
                            <Tag color="cyan">Раунд {step.round_number}</Tag>
                            <Tag color="cyan">
                              Документ:{' '}
                              <a onClick={() => openFileCard(step.target_file_id)} style={{ color: '#1890ff' }}>
                                {step.target_file_name}
                              </a>
                            </Tag>
                            {step.project_name && (
                              <Tag color="geekblue" style={{ cursor: 'pointer' }} onClick={() => navigate(`/projects?projectId=${step.project_id}`)}>
                                {step.project_name}
                              </Tag>
                            )}
                          </Space>
                        </div>
                        <Space>
                          {!step.is_editor && (
                            <Tag color={step.status === 'approved' ? 'success' : 'error'}>
                              {step.status === 'approved' ? 'Согласовано' : 'Отклонено'}
                            </Tag>
                          )}
                          {step.is_editor && (
                            <Tag color={step.status === 'completed' ? 'success' : step.status === 'cancelled' ? 'error' : 'warning'}>
                              {step.status === 'completed' ? 'Завершён' : step.status === 'cancelled' ? 'Отменён' : step.status}
                            </Tag>
                          )}
                          {step.task_id && <Button size="small" onClick={() => navigate(`/tasks?taskId=${step.task_id}`)}>Задача</Button>}
                        </Space>
                      </div>
                    </Card>
                  </List.Item>
                )}
              />
            </div>
            </>
          )
        },
        {
          key: '3',
          label: <span><CheckSquareOutlined /> Задачи на проверке ({taskValidations.length})</span>,
          children: (
            <>
            <List
              loading={loading}
              dataSource={taskValidations}
              locale={{ emptyText: 'Нет задач, ожидающих вашей проверки' }}
              renderItem={(task) => (
                <List.Item>
                  <Card style={{ width: '100%' }} size="small">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text strong style={{ fontSize: 16 }}>{task.title}</Text>
                        <Space style={{ display: 'block', marginTop: 4 }}>
                          <Tag color="orange">На проверке</Tag>
                          {task.project?.name && <Tag color="geekblue">{task.project.name}</Tag>}
                        </Space>
                      </div>
                      <Button type="primary" onClick={() => navigate(`/tasks?taskId=${task.id}`)}>
                        Перейти к задаче
                      </Button>
                    </div>
                  </Card>
                </List.Item>
              )}
            />
            
            <Divider>История проверенных задач</Divider>
            <div style={{ maxHeight: 'calc(25 * 70px)', overflowY: 'auto' }}>
              <List
                dataSource={taskValidationsHistory}
                locale={{ emptyText: 'Нет истории проверок' }}
                renderItem={(task) => (
                  <List.Item>
                    <Card style={{ width: '100%', opacity: 0.8 }} size="small">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <Text strong>{task.title}</Text>
                          <br/>
                          <Space style={{ marginTop: 4 }}>
                            {task.project?.name && <Tag color="geekblue">{task.project.name}</Tag>}
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {dayjs(task.updated_at).format('DD.MM.YYYY HH:mm')}
                            </Text>
                          </Space>
                        </div>
                        <Space>
                          <Tag color="success">Завершено</Tag>
                          <Button size="small" onClick={() => navigate(`/tasks?taskId=${task.id}`)}>Открыть</Button>
                        </Space>
                      </div>
                    </Card>
                  </List.Item>
                )}
              />
            </div>
            </>
          )
        },
        {
          key: '4',
          label: <span><CheckOutlined /> Чек-листы ({checklists.length})</span>,
          children: (
            <>
            <List
              loading={loading}
              dataSource={checklists}
              locale={{ emptyText: 'Нет активных пунктов чек-листа' }}
              renderItem={(item) => (
                <List.Item>
                  <Card style={{ width: '100%' }} size="small">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text strong style={{ fontSize: 16 }}>{item.text}</Text>
                        <Space style={{ display: 'block', marginTop: 4 }}>
                          <Tag color="cyan">Задача: {item.task_title}</Tag>
                          {item.project_name && <Tag color="geekblue">{item.project_name}</Tag>}
                        </Space>
                      </div>
                      <Space>
                        <Button type="primary" onClick={() => handleChecklistComplete(item.task_id, item.id)}>Выполнено</Button>
                        <Button onClick={() => navigate(`/tasks?taskId=${item.task_id}`)}>Перейти к задаче</Button>
                      </Space>
                    </div>
                  </Card>
                </List.Item>
              )}
            />
            
            <Divider>История выполненных пунктов</Divider>
            <div style={{ maxHeight: 'calc(25 * 70px)', overflowY: 'auto' }}>
              <List
                dataSource={checklistsHistory}
                locale={{ emptyText: 'Нет истории выполненных пунктов' }}
                renderItem={(item) => (
                  <List.Item>
                    <Card style={{ width: '100%', opacity: 0.8 }} size="small">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <Text strong style={{ textDecoration: 'line-through', color: 'gray' }}>{item.text}</Text>
                          <br/>
                          <Space style={{ marginTop: 4 }}>
                            <Tag color="cyan">Задача: {item.task_title}</Tag>
                            {item.project_name && <Tag color="geekblue">{item.project_name}</Tag>}
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {dayjs(item.updated_at).format('DD.MM.YYYY HH:mm')}
                            </Text>
                          </Space>
                        </div>
                        <Space>
                          <Tag color="success">Выполнено</Tag>
                          <Button size="small" onClick={() => navigate(`/tasks?taskId=${item.task_id}`)}>Задача</Button>
                        </Space>
                      </div>
                    </Card>
                  </List.Item>
                )}
              />
            </div>
            </>
          )
        },
        {
          key: '5',
          label: <span><ClockCircleOutlined /> Просрочено ({overdueTasks.length})</span>,
          children: (
            <List
              loading={loading}
              dataSource={overdueTasks}
              locale={{ emptyText: 'Нет просроченных задач' }}
              renderItem={(task) => (
                <List.Item>
                  <Card style={{ width: '100%', borderColor: '#ff4d4f' }} size="small">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text strong style={{ fontSize: 16 }}>
                          <a onClick={() => navigate(`/tasks?taskId=${task.id}`)} style={{ color: '#cf1322' }}>
                            {task.title}
                          </a>
                        </Text>
                        <Space style={{ display: 'block', marginTop: 4 }}>
                          <Tag color="error">
                            Просрочено: {dayjs(task.actual_due_date || task.due_date).format('DD.MM.YYYY')}
                          </Tag>
                          {task.project?.name && <Tag color="geekblue">{task.project.name}</Tag>}
                        </Space>
                      </div>
                      <Button danger onClick={() => navigate(`/tasks?taskId=${task.id}`)}>
                        Перейти к задаче
                      </Button>
                    </div>
                  </Card>
                </List.Item>
              )}
            />
          )
        }
      ]} />
      
      <Modal
        title={bpmCurrentAction.status === 'approved' ? 'Согласование документа' : 'Отклонение документа'}
        open={bpmModalVisible}
        onOk={submitBpmApprove}
        onCancel={() => setBpmModalVisible(false)}
        okText={bpmCurrentAction.status === 'approved' ? 'Согласовать' : 'Отклонить'}
        okButtonProps={{ 
          danger: bpmCurrentAction.status === 'rejected',
          disabled: !bpmComment.trim()
        }}
      >
        <p>Пожалуйста, оставьте обязательный комментарий:</p>
        <Input.TextArea 
          rows={4} 
          value={bpmComment} 
          onChange={(e) => setBpmComment(e.target.value)} 
          placeholder="Ваш комментарий..."
        />
      </Modal>

      <Modal
        title="Делегирование согласования"
        open={delegateModalVisible}
        onOk={submitDelegate}
        onCancel={() => setDelegateModalVisible(false)}
        okText="Делегировать"
        cancelText="Отмена"
      >
        <p>Выберите сотрудника для делегирования права подписи:</p>
          <Select
            style={{ width: '100%' }}
            placeholder="Выберите сотрудника для делегирования"
            value={delegateTarget}
            onChange={setDelegateTarget}
            optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
          />
        </Modal>

      <Modal
        title={`Запуск круга ${restartTarget?.round_number + 1 || 2}`}
        open={restartModalVisible}
        onOk={submitRestart}
        onCancel={() => setRestartModalVisible(false)}
        okText="Запустить"
        cancelText="Отмена"
        okButtonProps={{ loading: restartLoading, disabled: restartFiles.length === 0 }}
      >
        <p>Загрузите обновлённые файлы для нового круга согласования:</p>
        <Upload.Dragger
          multiple
          beforeUpload={() => false}
          onChange={({ fileList }) => setRestartFiles(fileList)}
          fileList={restartFiles}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">Нажмите или перетащите файлы</p>
        </Upload.Dragger>
      </Modal>

      <ProcessDetailModal 
        process={currentProcess} 
        open={detailModalOpen} 
        onCancel={() => {
          setDetailModalOpen(false);
          setCurrentProcess(null);
        }} 
        users={users}
      />
    </div>
  );
}
